import { Array, pipe } from 'effect'
import { isNotNullish } from 'effect/Predicate'

import { getTailwindColorByIndex } from '../../../../constant/colors.ts'
import type { ViewQueryList } from '../../../../schemas/query/index.ts'
import type { ViewUICol, ViewUIForm, ViewUiFormComponent } from '../../../../schemas/ui/component.ts'
import type { ViewConfigForm } from '../../../../schemas/view_config/index.ts'
import { flatNodes } from '../../../query/executeViewQuery/util.ts'
import type { CompileContext } from '../../common.ts'
import { compileSheetViewConfigFilterTree } from '../sheet/compileSheetViewConfigFilterTree.ts'
import { compileSheetViewConfigTree } from '../sheet/compileSheetViewConfigTree.ts'
import { expandChildrenByReferenceField } from '../sheet/expandChildrenByReferenceField.ts'
import { pruneSheetConfigTree } from '../sheet/pruneSheetConfigTree.ts'
import {
  type ColFieldAndComponentNode,
  compileFormViewConfigComponents,
  type FieldAndComponentNode,
  flatFieldAndComponentNodes,
  getAdditionalQueryField,
  getQueryFieldsOfFieldAndComponentNode,
} from './compileFormViewConfigComponents.ts'

export interface CompiledFormViewConfig {
  type: 'form'
  queries: [ViewQueryList]
  query: ViewQueryList
  ui: [ViewUIForm]
  context: CompileContext
}

export function compileFormViewConfig(
  config: ViewConfigForm,
  context: CompileContext,
): CompiledFormViewConfig | undefined {
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO
  const prunedTree = pruneSheetConfigTree(config)
  const expandedTree = expandChildrenByReferenceField(prunedTree, config, context)
  const tree = compileSheetViewConfigTree(expandedTree, context)
  if (tree === undefined || expandedTree === undefined) {
    return undefined
  }
  const queryNodes = flatNodes(tree)
  const configNodes = flatNodes(expandedTree)
  const componentNodes = compileFormViewConfigComponents(config.components, { queryNodes, configNodes, context })
  if (componentNodes.length === 0) {
    return undefined
  }
  const flattenComponentNodes = componentNodes.flatMap((x) => flatFieldAndComponentNodes(x))
  const additionalFields = getAdditionalQueryField(tree, config.tree, context)
  const fields = pipe(
    [
      additionalFields?.nameQueryField,
      additionalFields?.recordUrlQueryField,
      ...flattenComponentNodes.flatMap((x) => getQueryFieldsOfFieldAndComponentNode(x)),
    ],
    Array.filter(isNotNullish),
    Array.dedupeWith((a, b) => a.name === b.name),
  )
  const filterTree = compileSheetViewConfigFilterTree(
    config.filterTree,
    queryNodes,
    configNodes,
    fields,
    context,
    undefined,
    // eslint-disable-next-line sonarjs/todo-tag
  ) // TODO: id埋め込み
  const listQuery: ViewQueryList = {
    type: 'list',
    tree,
    fields,
    filterTree: {
      ...filterTree,
      leafs: [
        ...filterTree.leafs,
        {
          nodePaths: [tree.path],
          read: {
            sql: `"${tree.name}".id = '{{ recordId }}'`,
            dependedParameters: ['recordId'],
          },
        },
      ],
    },
    sorters: [],
  }

  const rootModel = context.modelSearcher.searchModel(config.tree?.modelName)

  return {
    type: 'form',
    queries: [listQuery],
    query: listQuery,
    ui: [
      {
        type: 'Form',
        label: tree.meta.label,
        children: componentNodes.map((x) => convertComponentNodes(x)),
        nameFieldName: additionalFields?.nameQueryField?.name,
        recordUrlFieldName: additionalFields?.recordUrlQueryField?.name,
        color: rootModel?.color ?? getTailwindColorByIndex(0),
        icon: rootModel?.icon,
      },
    ],
    context,
  }
}

function convertColNode(node: ColFieldAndComponentNode): ViewUICol {
  return {
    ...node.component,
    children: node.children.map((x) => convertComponentNodes(x)),
  }
}

function convertComponentNodes(node: FieldAndComponentNode): ViewUiFormComponent {
  switch (node.type) {
    case 'Row': {
      return {
        ...node.component,
        children: node.children.map((x) => convertColNode(x)),
      }
    }
    case 'Col': {
      return convertColNode(node)
    }
    case 'ChildRecordsForm': {
      return {
        ...node.component,
        children: node.children.map((x) => convertComponentNodes(x)),
      }
    }
    case 'FormItem': {
      return node.component
    }
  }
}
